import { useEffect } from "react";
import { attachAutocomplete, geocode, gmaps } from "./loads/geo";

/**
 * Google Places autocomplete for city inputs (same engine the order form uses).
 * `refs` = [{ ref, onPick }]; the Maps JS API is loaded by AuthProvider, so we wait for it.
 * onPick receives { address, city, lat, lng, country, country_code }.
 * Re-attach by changing `deps` (e.g. when a dialog with the inputs opens).
 */
export function usePlaces(refs, deps = []) {
  useEffect(() => {
    let offs = [];
    let timer = null;
    let tries = 0;
    const attach = () => {
      if (!(window.google && window.google.maps && window.google.maps.places)) {
        if (tries++ < 20) timer = setTimeout(attach, 500);
        return;
      }
      offs = refs
        .filter((r) => r.ref.current)
        .map((r) => attachAutocomplete(r.ref.current, r.onPick, { types: ["(cities)"] }));
    };
    attach();
    return () => {
      clearTimeout(timer);
      offs.forEach((o) => o());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Typed city without a picked suggestion → coordinates via the Geocoder (null when Maps is unavailable). */
export const geocodeCity = async (text) => {
  const t = (text || "").trim();
  if (!t) return null;
  const g = await geocode(t);
  return g ? { lat: g.lat, lng: g.lng, country: g.country || null } : null;
};

/** Browser GPS → { city, lat, lng, country } through the reverse Geocoder (spec §4.2 «автоопределение + ручное изменение»). */
export const detectMyCity = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("GPS недоступен в этом браузере"));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const m = gmaps();
        if (!m) return resolve({ city: "", lat, lng, country: null });
        new m.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
          if (status !== "OK" || !results || !results.length) return resolve({ city: "", lat, lng, country: null });
          const comps = results.flatMap((r) => r.address_components || []);
          const pick = (t) => comps.find((c) => c.types.includes(t));
          const city = (pick("locality") || pick("administrative_area_level_2") || pick("administrative_area_level_1") || {}).long_name || "";
          const country = (pick("country") || {}).long_name || null;
          resolve({ city, lat, lng, country });
        });
      },
      () => reject(new Error("Не удалось определить местоположение. Разрешите доступ к GPS или введите город вручную.")),
      { enableHighAccuracy: false, timeout: 10000 },
    );
  });
